import React, { useEffect, useState } from "react";
import { api, fmtNum } from "../lib/api";
import { Snowflake, TrendingUp, TrendingDown } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const KEYS = [
  { key: "live_chicken_kg", label: "Live Chicken", color: "#0284c7" },
  { key: "chicken_carcass_kg", label: "Chicken Carcass", color: "#06b6d4" },
  { key: "mutton_carcass_kg", label: "Mutton Carcass", color: "#e11d48" },
  { key: "offals_kg", label: "Offals (kg)", color: "#f59e0b" },
];

export default function FreezerAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/reports/freezer-analytics?days=${days}`).then((r) => setData(r.data));
  }, [days]);

  if (!data) {
    return (
      <div className="p-8 text-zinc-500" data-testid="freezer-analytics-loading">
        Loading…
      </div>
    );
  }

  const trend = data.trend || [];
  const latest = data.latest;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="freezer-analytics-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
            <Snowflake className="h-3 w-3" /> Cold Storage
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
            Freezer Analytics
          </h1>
        </div>
        <div className="flex gap-2">
          {[7, 14, 30, 60].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`fa-days-${d}`}
              className={`px-3 py-2 text-xs uppercase tracking-wider border ${
                days === d
                  ? "border-sky-600 bg-sky-600 text-white"
                  : "border-zinc-300 hover:bg-zinc-50"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="border border-zinc-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Latest Total</div>
          <div className="font-display text-3xl font-bold font-mono-num mt-1">
            {latest ? `${fmtNum(latest.total_kg, 2)} kg` : "—"}
          </div>
          {latest && <div className="text-xs text-zinc-500 mt-1">on {latest.date}</div>}
        </div>
        <div className="border border-zinc-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{days}-Day Avg</div>
          <div className="font-display text-3xl font-bold font-mono-num mt-1">
            {fmtNum(data.avg_total_kg, 2)} kg
          </div>
        </div>
        <div className="border border-emerald-200 bg-emerald-50 p-5" data-testid="fa-sold-kpi">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Sold from Freezer ({days}d)</div>
          <div className="font-display text-3xl font-bold font-mono-num mt-1 text-emerald-700">
            {fmtNum(data.total_sold_kg || 0, 2)} kg
          </div>
        </div>
        <div className="border border-zinc-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">vs Last-Week Avg</div>
          <div className={`font-display text-3xl font-bold font-mono-num mt-1 flex items-center gap-2 ${
            data.delta_vs_week_avg_pct > 0
              ? "text-emerald-600"
              : data.delta_vs_week_avg_pct < 0
              ? "text-rose-600"
              : ""
          }`}>
            {data.delta_vs_week_avg_pct > 0 ? <TrendingUp className="h-6 w-6" /> : data.delta_vs_week_avg_pct < 0 ? <TrendingDown className="h-6 w-6" /> : null}
            {data.delta_vs_week_avg_pct > 0 ? "+" : ""}{data.delta_vs_week_avg_pct}%
          </div>
        </div>
      </div>

      {/* Trend chart */}
      <div className="border border-zinc-200 bg-white p-5 mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
          Total Stock Trend
        </div>
        <div className="font-display text-2xl font-bold mb-4">{days}-day movement</div>
        {trend.length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center">
            No freezer snapshots in this window. Record stock daily on the Freezer Stock page.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid stroke="#f4f4f5" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="total_kg"
                stroke="#0284c7"
                fill="#0284c7"
                fillOpacity={0.15}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Per-category trend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {KEYS.map((k) => (
          <div key={k.key} className="border border-zinc-200 bg-white p-5" data-testid={`fa-${k.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{k.label}</div>
              <div className="font-mono-num font-bold">
                {latest ? `${fmtNum(latest[k.key] || 0, 2)} kg` : "—"}
              </div>
            </div>
            {trend.length > 0 && (
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={trend} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                  <XAxis dataKey="date" hide />
                  <YAxis hide />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey={k.key}
                    stroke={k.color}
                    fill={k.color}
                    fillOpacity={0.18}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        ))}
      </div>

      {/* Offals — every product */}
      {(data.offals_products || []).length > 0 && (
        <div className="mt-6 border border-amber-200 bg-amber-50 p-5" data-testid="fa-offals-products">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-900 font-semibold mb-3">
            Offals — All Products (latest recorded)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {data.offals_products.map((o) => (
              <div key={o.label} className="border border-amber-200 bg-white p-3" data-testid={`fa-offal-${o.label}`}>
                <div className="text-xs font-semibold truncate">{o.label}</div>
                <div className="font-display text-xl font-bold font-mono-num mt-1">
                  {o.unit === "piece" ? `${Math.round(o.qty)} pcs` : `${fmtNum(o.qty, 2)} kg`}
                </div>
                <div className="text-[10px] text-zinc-500">as of {o.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw snapshots table */}
      <div className="mt-8 border border-zinc-200 bg-white">
        <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          Daily Snapshots ({trend.length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-right">Live Chicken</th>
                <th className="px-4 py-2 text-right">Chicken Carcass</th>
                <th className="px-4 py-2 text-right">Mutton Carcass</th>
                <th className="px-4 py-2 text-right">Offals kg</th>
                <th className="px-4 py-2 text-right">Offals pcs</th>
                <th className="px-4 py-2 text-right text-emerald-700">Sold kg</th>
                <th className="px-4 py-2 text-right font-bold">Total kg</th>
              </tr>
            </thead>
            <tbody>
              {[...trend].reverse().map((t) => (
                <tr key={t.date} className="border-b border-zinc-100">
                  <td className="px-4 py-2">{t.date}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmtNum(t.live_chicken_kg, 2)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmtNum(t.chicken_carcass_kg, 2)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmtNum(t.mutton_carcass_kg, 2)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{fmtNum(t.offals_kg, 2)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{t.offals_pcs}</td>
                  <td className="px-4 py-2 text-right font-mono-num text-emerald-700">{fmtNum(t.sold_kg || 0, 2)}</td>
                  <td className="px-4 py-2 text-right font-mono-num font-bold">{fmtNum(t.total_kg, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
